import {
  SrpFilterName,
  SrpFilterValues,
  SrpFuelEfficiencyValue,
  SrpRadiusFilterValue,
} from 'types/srpFilters';
import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import { DeepPartial } from 'types/util';
import {
  getCategoryApiName,
  getCategoryStateName,
} from 'components/Srp/SearchFilter/CategoryFilter/categoryNames';
import { SearchParams } from 'lib/apis/SearchApi/types';

export type SrpFilterApiParams = Omit<
  SearchParams,
  'locale' | 'makes' | 'make_models' | 'model_trims'
> & {
  make?: string;
  model?: string;
  trim?: string[];
};

export function transformSrpFilterStateToApiParams(state: SrpFilterValues): SrpFilterApiParams {
  return {
    sort_new_cars_last: state[SrpFilterName.SortNewCarsLast],
    page: state[SrpFilterName.Page],
    // eslint-disable-next-line no-restricted-globals
    limit: !isNaN(state[SrpFilterName.Limit])
      ? state[SrpFilterName.Limit]
      : srpFilterInitialValues[SrpFilterName.Limit],
    radius: state[SrpFilterName.Radius] || srpFilterInitialValues[SrpFilterName.Radius],
    make: primitive(
      state[SrpFilterName.MakeModel]?.make,
      srpFilterInitialValues[SrpFilterName.MakeModel]?.make
    ),
    model: primitive(
      state[SrpFilterName.MakeModel]?.model,
      srpFilterInitialValues[SrpFilterName.MakeModel]?.model
    ),
    trim: primitive(state[SrpFilterName.Trims], srpFilterInitialValues[SrpFilterName.Trims]),
    category: getCategoryApiName(state[SrpFilterName.Category]),
    year_min: primitive(
      state[SrpFilterName.Year].min,
      srpFilterInitialValues[SrpFilterName.Year].min
    ),
    year_max: primitive(
      state[SrpFilterName.Year].max,
      srpFilterInitialValues[SrpFilterName.Year].max
    ),
    price_min: primitive(
      state[SrpFilterName.Price].min,
      srpFilterInitialValues[SrpFilterName.Price].min
    ),
    price_max: primitive(
      state[SrpFilterName.Price].max,
      srpFilterInitialValues[SrpFilterName.Price].max
    ),
    exclude_no_price: primitive<boolean>(
      state[SrpFilterName.Price].hideNoPriceListings,
      srpFilterInitialValues[SrpFilterName.Price].hideNoPriceListings
    ),
    condition: booleanCollection(state[SrpFilterName.Condition]),
    engine_cylinders: booleanCollection(state[SrpFilterName.Cylinders]),
    door_count: booleanCollection(state[SrpFilterName.Doors]),
    body_style: booleanCollection(state[SrpFilterName.BodyTypes]),
    exterior_color: booleanCollection(
      state[SrpFilterName.Colors].exterior as unknown as Record<string, boolean>
    ),
    interior_color: booleanCollection(
      state[SrpFilterName.Colors].interior as unknown as Record<string, boolean>
    ),
    driveline: legacyBooleanCollection(
      'stateToApiParams',
      state[SrpFilterName.Drivetrain]
    ) as string[],
    fuel_type: legacyBooleanCollection(
      'stateToApiParams',
      state[SrpFilterName.Powertrain]
    ) as string[],
    features: legacyBooleanCollection(
      'stateToApiParams',
      state[SrpFilterName.FeaturesTechnology]
    ) as string[],
    city: primitive(
      state[SrpFilterName.Location].city,
      srpFilterInitialValues[SrpFilterName.Location].city
    ),
    state: primitive(
      state[SrpFilterName.Location].stateCode,
      srpFilterInitialValues[SrpFilterName.Location].stateCode
    ),
    latitude: primitive(
      state[SrpFilterName.Location].latitude,
      srpFilterInitialValues[SrpFilterName.Location].latitude
    ),
    longitude: primitive(
      state[SrpFilterName.Location].longitude,
      srpFilterInitialValues[SrpFilterName.Location].longitude
    ),
    mileage: primitive(
      state[SrpFilterName.Mileage].max,
      srpFilterInitialValues[SrpFilterName.Mileage].max
    ),
    transmission: booleanCollection(state[SrpFilterName.Transmission]),
    cabin: booleanCollection(
      state[SrpFilterName.TruckOptions].cabinType as unknown as Record<string, boolean>
    ),
    bed: booleanCollection(
      state[SrpFilterName.TruckOptions].bedType as unknown as Record<string, boolean>
    ),
    rear_wheel: booleanCollection(
      state[SrpFilterName.TruckOptions].rearWheel as unknown as Record<string, boolean>
    ),
    exclude_regional:
      primitive<boolean | undefined>(
        state[SrpFilterName.Advanced].hideOnlineListings,
        srpFilterInitialValues[SrpFilterName.Advanced].hideOnlineListings
      ) ?? undefined,
    combined_mpg: primitive(
      state[SrpFilterName.FuelEfficiency],
      srpFilterInitialValues[SrpFilterName.FuelEfficiency]
    ),
    bed_length_min: primitive(
      state[SrpFilterName.TruckOptions].bedLength.min,
      srpFilterInitialValues[SrpFilterName.TruckOptions].bedLength.min
    ),
    bed_length_max: primitive(
      state[SrpFilterName.TruckOptions].bedLength.max,
      srpFilterInitialValues[SrpFilterName.TruckOptions].bedLength.max
    ),
    exclude_no_images:
      primitive<boolean>(
        state[SrpFilterName.Advanced]?.hideListingsNoImages ?? false,
        srpFilterInitialValues[SrpFilterName.Advanced]?.hideListingsNoImages ?? false
      ) ?? undefined,
    sort_filter: primitive<string | undefined>(
      state[SrpFilterName.SortBy],
      srpFilterInitialValues[SrpFilterName.SortBy]
    ),
  };

  function primitive<T>(value: T, initial: T): T | undefined {
    return value === initial ? undefined : value;
  }

  function booleanCollection(collection: Record<string, boolean>): string[] {
    return Object.entries(collection).reduce<string[]>((acc, [key, value]) => {
      if (value) {
        acc.push(key);
      }
      return acc;
    }, []);
  }
}

export interface SearchFilterUrlParams {
  page?: string;
  limit?: string;
  keywords?: string[];
  make?: string;
  model?: string;
  trim?: string[];
  category?: string;
  latitude?: string;
  longitude?: string;
  condition?: string[];
  engine_cylinders?: string[];
  year_min?: string;
  year_max?: string;
  price_min?: string;
  price_max?: string;
  exclude_no_price?: string;
  body_style?: string[];
  door_count?: string[];
  exterior_color?: string[];
  interior_color?: string[];
  driveline?: string[];
  fuel_type?: string[];
  combined_mpg?: string;
  features?: string[];
  city?: string;
  state?: string;
  mileage?: string;
  transmission?: string[];
  cabin?: string[];
  bed?: string[];
  rear_wheel?: string[];
  bed_length_min?: string;
  bed_length_max?: string;
  exclude_regional?: string;
  exclude_no_images?: string;
  radius?: string;
  sort_filter?: string;
  location?: string;
  sort_new_cars_last?: boolean;
}

function isPaidLandingUrl(apiParams: SearchFilterUrlParams): boolean {
  const hasLatLon = apiParams.latitude && apiParams.longitude;
  const hasOnlyLocation = apiParams.location && !apiParams.city && !apiParams.state;
  return Boolean(hasLatLon && hasOnlyLocation);
}

export function transformSrpFilterApiParamsToState(
  apiParams: SearchFilterUrlParams
): DeepPartial<SrpFilterValues> {
  return Object.keys(apiParams).reduce<DeepPartial<SrpFilterValues>>((values, k) => {
    const key = k as keyof SrpFilterApiParams;
    /* eslint-disable no-param-reassign */
    if (key === 'sort_new_cars_last') {
      values[SrpFilterName.SortNewCarsLast] = apiParams.sort_new_cars_last;
    }

    if (key === 'page') {
      values[SrpFilterName.Page] = number(apiParams.page);
    }

    if (key === 'limit') {
      values[SrpFilterName.Limit] = srpFilterInitialValues[SrpFilterName.Limit];
    }

    if (key === 'make' || key === 'model') {
      values[SrpFilterName.MakeModel] = {
        make: apiParams.make,
        model: apiParams.model,
      };
    }

    if (key === 'trim') {
      values[SrpFilterName.Trims] = apiParams.trim;
    }

    if (key === 'category' && apiParams.category) {
      values[SrpFilterName.Category] = getCategoryStateName(apiParams.category);
    }

    if (key === 'year_min' || key === 'year_max') {
      values[SrpFilterName.Year] = {
        min: number(apiParams.year_min, srpFilterInitialValues.Year.min),
        max: number(apiParams.year_max, srpFilterInitialValues.Year.max),
      };
    }

    if (key === 'condition') {
      values[SrpFilterName.Condition] = booleanCollection(apiParams.condition);
    }

    if (key === 'engine_cylinders') {
      values[SrpFilterName.Cylinders] = booleanCollection(apiParams.engine_cylinders);
    }

    if (key === 'body_style') {
      values[SrpFilterName.BodyTypes] = booleanCollection(apiParams.body_style);
    }

    if (key === 'radius') {
      let radiusValue;

      if (apiParams.radius === 'Any') {
        radiusValue = apiParams.radius;
      } else {
        radiusValue = number(
          apiParams.radius,
          srpFilterInitialValues[SrpFilterName.Radius] as number
        );
      }

      values[SrpFilterName.Radius] = radiusValue as SrpRadiusFilterValue;
    }

    if (key === 'features') {
      values[SrpFilterName.FeaturesTechnology] = legacyBooleanCollection(
        'apiParamsToState',
        apiParams.features as string[]
      ) as { [key: string]: boolean };
    }

    if (key === 'fuel_type') {
      values[SrpFilterName.Powertrain] = legacyBooleanCollection(
        'apiParamsToState',
        apiParams.fuel_type as string[]
      ) as { [key: string]: boolean };
    }

    if (key === 'exclude_regional' || key === 'exclude_no_images') {
      values[SrpFilterName.Advanced] = {
        ...values[SrpFilterName.Advanced],
        hideListingsNoImages: Boolean(apiParams.exclude_no_images),
        hideOnlineListings: Boolean(apiParams.exclude_regional),
      };
    }

    if (key === 'latitude' || key === 'longitude' || key === 'city' || key === 'state') {
      const [city, stateCode] = isPaidLandingUrl(apiParams)
        ? apiParams.location!.split(',').map((str) => str.trim())
        : [apiParams.city, apiParams.state];

      values[SrpFilterName.Location] = {
        latitude: number(apiParams.latitude),
        longitude: number(apiParams.longitude),
        city,
        stateCode,
      };
    }

    if (key === 'price_min' || key === 'price_max' || key === 'exclude_no_price') {
      values[SrpFilterName.Price] = {
        min: number(apiParams.price_min, srpFilterInitialValues.Price.min),
        max: number(apiParams.price_max, srpFilterInitialValues.Price.max),
        hideNoPriceListings: apiParams.exclude_no_price === 'true',
      };
    }

    if (key === 'bed_length_min' || key === 'bed_length_max') {
      values[SrpFilterName.TruckOptions] = {
        ...values[SrpFilterName.TruckOptions],
        bedLength: {
          min: number(apiParams.bed_length_min),
          max: number(apiParams.bed_length_max),
        },
      };
    }

    if (key === 'bed') {
      values[SrpFilterName.TruckOptions] = {
        ...values[SrpFilterName.TruckOptions],
        bedType: booleanCollection(apiParams.bed),
      };
    }

    if (key === 'cabin') {
      values[SrpFilterName.TruckOptions] = {
        ...values[SrpFilterName.TruckOptions],
        cabinType: booleanCollection(apiParams.cabin),
      };
    }

    if (key === 'rear_wheel') {
      values[SrpFilterName.TruckOptions] = {
        ...values[SrpFilterName.TruckOptions],
        rearWheel: booleanCollection(apiParams.rear_wheel),
      };
    }

    if (key === 'combined_mpg') {
      values[SrpFilterName.FuelEfficiency] = number(
        apiParams.combined_mpg,
        srpFilterInitialValues[SrpFilterName.FuelEfficiency]
      ) as SrpFuelEfficiencyValue;
    }

    if (key === 'mileage') {
      values[SrpFilterName.Mileage] = {
        max: number(apiParams.mileage, srpFilterInitialValues.Mileage.max),
      };
    }

    if (key === 'door_count') {
      values[SrpFilterName.Doors] = booleanCollection(apiParams.door_count);
    }

    if (key === 'transmission') {
      values[SrpFilterName.Transmission] = booleanCollection(apiParams.transmission);
    }

    if (key === 'driveline') {
      values[SrpFilterName.Drivetrain] = legacyBooleanCollection(
        'apiParamsToState',
        apiParams.driveline as string[]
      ) as { [key: string]: boolean };
    }

    if (key === 'exterior_color' || key === 'interior_color') {
      values[SrpFilterName.Colors] = {
        ...values[SrpFilterName.Colors],
        exterior: booleanCollection(apiParams.exterior_color),
        interior: booleanCollection(apiParams.interior_color),
      };
    }

    if (key === 'sort_filter') {
      values[SrpFilterName.SortBy] = apiParams.sort_filter;
    }

    return values;
  }, {});

  function number(value?: string, defaultValue?: number): number | undefined {
    return value ? parseFloat(value) : defaultValue;
  }

  function booleanCollection(collection?: (string | number)[]): Record<string | number, boolean> {
    if (!collection) {
      return {};
    }

    return collection.reduce<Record<string | number, boolean>>((acc, name) => {
      acc[name] = true;
      return acc;
    }, {});
  }
}

/* Using this function to transform certain options to the expected URL search params */
function legacyBooleanCollection(
  type: string,
  collection: Record<string, boolean> | string[]
): string[] | { [key: string]: boolean } {
  const stateToApiParamLegacyMap: { [key: string]: string } = {
    rearWheel: 'RWD',
    frontWheel: 'FWD',
    allWheel: 'AWD',
    fourWheel: '4X4',
    hydrogenFuelCell: 'hydrogen fuel cell',
    naturalGas: 'natural gas',
    pluginHybrid: 'plug-in hybrid',
    electric: 'electric',
    diesel: 'diesel',
    gasoline: 'gasoline',
    hybrid: 'hybrid',
    backupCamera: 'backup_camera',
    bluetooth: 'bluetooth',
    entertainmentDVD: 'entertainment',
    handicapAccessible: 'handicap_accessible',
    iPodAUXInput: 'ipod_aux_input',
    laneDepartureWarning: 'lane_departure_warning',
    leather: 'leather',
    navigationSystem: 'navigation',
    roofRack: 'roof_rack',
    sunroofMoonroof: 'sunroof',
    '3rdRowSeats': 'third_row_seats',
    towing: 'towing',
    warranty: 'warranty',
  };
  const apiParamToStateLegacyMap: { [key: string]: string } = {
    RWD: 'rearWheel',
    FWD: 'frontWheel',
    AWD: 'allWheel',
    '4X4': 'fourWheel',
    'hydrogen fuel cell': 'hydrogenFuelCell',
    'natural gas': 'naturalGas',
    'plug-in hybrid': 'pluginHybrid',
    electric: 'electric',
    diesel: 'diesel',
    gasoline: 'gasoline',
    hybrid: 'hybrid',
    backup_camera: 'backupCamera',
    bluetooth: 'bluetooth',
    entertainment: 'entertainmentDVD',
    handicap_accessible: 'handicapAccessible',
    ipod_aux_input: 'iPodAUXInput',
    lane_departure_warning: 'laneDepartureWarning',
    leather: 'leather',
    navigation: 'navigationSystem',
    roof_rack: 'roofRack',
    sunroof: 'sunroofMoonroof',
    third_row_seats: '3rdRowSeats',
    towing: 'towing',
    warranty: 'warranty',
  };

  if (type === 'apiParamsToState') {
    const acc: { [key: string]: boolean } = {};
    const collectionArray = collection as string[];
    collectionArray.forEach((value: string) => {
      acc[apiParamToStateLegacyMap[value]] = true;
    });
    return acc;
  }
  return Object.entries(collection).reduce<string[]>((acc, [key, value]) => {
    if (value) {
      acc.push(stateToApiParamLegacyMap[key]);
    }
    return acc;
  }, []);
}
